import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  MapPin,
  Layers,
  Shirt,
  PackagePlus,
  Lock,
  ArrowRight,
  Award,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Chatou ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison locale
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Service de livraison de cartons au cœur de la ville
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la préparation d&apos;un déménagement dans une
              grande commune requiert une gestion du temps optimisée,
              notre service logistique de proximité prend en charge
              l&apos;acheminement direct de tout votre matériel.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture totale",
                body: "Nous desservons avec une parfaite régularité l'intégralité des quartiers catoviens, ainsi que les communes limitrophes de premier plan telles que Rueil-Malmaison, Croissy-sur-Seine, Le Vésinet et Montesson.",
                Icon: MapPin,
              },
              {
                title: "Confort et gain de temps",
                body: "Vos fournitures vous sont livrées à plat, impeccablement propres et sèches. Vous vous épargnez ainsi les trajets fatigants et l'encombrement de votre véhicule personnel pour débuter vos préparatifs immédiatement.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons garde-robe haut de gamme ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Garde-robe premium
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des cartons garde-robe haut de gamme pour vos vêtements de
            valeur
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transport du textile et de la haute couture nécessite
            des solutions sophistiquées pour éviter les corvées de
            repassage et préserver la qualité des tissus fragiles.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Système prêt à suspendre */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shirt className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Système prêt à suspendre
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Équipés d&apos;une tringle amovible ultra-solide, nos
              cartons penderie de grande hauteur vous permettent de
              transférer vos costumes, robes, manteaux et vestes
              directement depuis vos armoires sans les décrocher de
              leurs cintres.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Tringle métallique amovible
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal haute couture &amp; costumes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Grande hauteur, double cannelure
              </div>
            </div>
          </div>

          {/* Zéro pli */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Award className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Zéro pli à l&apos;arrivée
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Vos vêtements restent suspendus, parfaitement ordonnés et
              totalement isolés de la poussière ou des frottements
              durant le voyage. Il ne vous reste plus qu&apos;à les
              replacer directement dans votre dressing {city.aCity}.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucun pli ni faux pli
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Protection anti-poussière
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Transfert direct vers le dressing
              </div>
            </div>
          </div>
        </div>

        <p className="mt-8 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
          Cette solution premium transforme la corvée du vidage
          d&apos;armoires en une simple formalité. En protégeant ainsi
          vos pièces textiles les plus précieuses, vous gagnez un temps
          considérable lors de votre aménagement et vous vous évitez
          l&apos;étape fastidieuse du pressing ou du défroissage dès
          vos premiers jours dans votre nouvelle demeure.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chatou-cta-penderie"
          >
            <Shirt className="h-4 w-4" strokeWidth={2.6} />
            Voir nos cartons penderies
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chatou-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les kits
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Emballages de protection ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection professionnelle
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des emballages de protection professionnels pour sceller
            votre sérénité
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un carton de qualité supérieure ne prend sa pleine
            efficacité que lorsqu&apos;il est associé à des systèmes
            de calage performants, capables de neutraliser les
            vibrations de la route.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Amortissement haute densité */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <PackagePlus className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Amortissement haute densité
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Notre catalogue propose des rouleaux de film à bulles
              haute performance conçus pour envelopper individuellement
              vos pièces de vaisselle fine, vos miroirs et vos
              équipements technologiques délicats.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Film à bulles haute performance
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Vaisselle fine &amp; miroirs
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                High-tech &amp; objets fragiles
              </div>
            </div>
          </div>

          {/* Fermeture hermétique */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Lock className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Fermeture hermétique
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos films étirables professionnels et nos rubans adhésifs
              à forte adhérence garantissent la stabilité de vos colis,
              empêchant tout affaissement ou ouverture accidentelle lors
              des phases de manutention.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Films étirables professionnels
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Adhésifs forte adhérence
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucune ouverture accidentelle
              </div>
            </div>
          </div>
        </div>

        <p className="mt-8 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
          En combinant des contenants robustes à ces accessoires de
          calage de niveau industriel, vous dressez un véritable
          bouclier autour de votre patrimoine. C&apos;est
          l&apos;assurance d&apos;aborder la journée du déménagement
          avec l&apos;esprit totalement libre, sachant que chaque objet
          a été préparé selon les règles de l&apos;art pour arriver
          impeccable à destination.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chatou-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir film, bulles &amp; adhésifs
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chatou-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Zones de livraison Yvelines ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos cartons de déménagement disponibles dans les Yvelines
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous résidiez dans une grande ville ou une commune plus
            résidentielle des Yvelines, nous mettons à votre disposition
            nos cartons de déménagement et fournitures
            d&apos;emballage. Notre couverture départementale nous
            permet de répondre rapidement à vos besoins et de vous
            fournir le matériel nécessaire pour préparer votre
            déménagement dans les meilleures conditions.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Découvrez les principales communes du 78 où nos produits
            sont régulièrement livrés à proximité de {city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-chatou-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-chatou-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chatou-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
